/**
 * Storefront minimum-order guard.
 * Shopify Checkout itself cannot run theme code, so this blocks Buy Now
 * and every store checkout button/link before the customer leaves the site.
 */
import { fetchCartState, openCartDrawer } from '@theme/adawat-cart';

function minCents() {
  const cfg = window.AdawatMinOrder || {};
  if (cfg.enabled === false) return 0;
  return Math.max(0, Number(cfg.cents) || 0);
}

function isArabic() {
  return (window.AdawatMinOrder?.locale || document.documentElement.lang || '') === 'ar';
}

function formatMoney(cents) {
  if (typeof window.adawatFormatMoney === 'function') return window.adawatFormatMoney(cents);
  const value = Math.max(0, Number(cents) || 0) / 100;
  const formatted = value % 1 === 0 ? String(value) : value.toFixed(2);
  return `$${formatted}`;
}

export function minOrderMessage(cartTotalCents) {
  const min = minCents();
  if (min <= 0) return '';
  const remaining = Math.max(0, min - Number(cartTotalCents || 0));
  if (remaining <= 0) return '';
  if (isArabic()) {
    return `أضف ${formatMoney(remaining)} لإتمام الطلب. الحد الأدنى ${formatMoney(min)}.`;
  }
  return `Add ${formatMoney(remaining)} more to check out. Minimum order is ${formatMoney(min)}.`;
}

export async function cartMeetsMinimum() {
  const min = minCents();
  if (min <= 0) return { ok: true, cart: null, message: '' };
  const cart = await fetchCartState();
  const total = Number(cart?.total_price) || 0;
  if (total >= min) return { ok: true, cart, message: '' };
  return { ok: false, cart, message: minOrderMessage(total) };
}

function checkoutPath() {
  return `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}checkout`;
}

function isCheckoutAnchor(el) {
  if (!(el instanceof HTMLAnchorElement) || !el.href) return false;
  try {
    const url = new URL(el.href, window.location.origin);
    return /\/checkout\/?$/.test(url.pathname);
  } catch (_) {
    return /\/checkout\/?(\?|#|$)/.test(el.getAttribute('href') || '');
  }
}

function isCheckoutControl(el) {
  if (!(el instanceof Element)) return false;
  if (el.closest('[data-adawat-buy-now]')) return false;
  if (el.closest('button[name="checkout"], input[name="checkout"]')) return true;
  const link = el.closest('a[href]');
  return Boolean(link && isCheckoutAnchor(link));
}

async function blockOrContinue(event, proceed) {
  const min = minCents();
  if (min <= 0) return;
  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

  const result = await cartMeetsMinimum();
  if (result.ok) {
    proceed();
    return;
  }

  openCartDrawer();
}

document.addEventListener(
  'click',
  (event) => {
    if (minCents() <= 0) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!isCheckoutControl(target)) return;
    const control = target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (control instanceof HTMLButtonElement && control.disabled) return;
    if (control instanceof HTMLInputElement && control.disabled) return;

    const link = control instanceof HTMLAnchorElement ? control : null;
    blockOrContinue(event, () => {
      window.location.assign(link?.href || checkoutPath());
    });
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    if (minCents() <= 0) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const wantsCheckout = event.submitter?.getAttribute?.('name') === 'checkout';
    if (!wantsCheckout) return;
    if (submitter?.disabled) return;

    blockOrContinue(event, () => {
      window.location.assign(checkoutPath());
    });
  },
  true
);

window.AdawatMinOrder = Object.assign(window.AdawatMinOrder || {}, {
  message: minOrderMessage,
  check: cartMeetsMinimum,
});
